import fabric from '@/lib/fabric';

export const GUIDE_KEY = 'craftaraGuide';

export type GuideOrientation = 'v' | 'h';

type GuideObject = fabric.Line & {
  craftaraGuide?: GuideOrientation;
  pixkitGuide?: GuideOrientation;
};

let guidesLocked = false;

export function setGuidesLocked(locked: boolean): void {
  guidesLocked = locked;
}

export function getGuidesLocked(): boolean {
  return guidesLocked;
}

/** Apply current lock state to all guide objects on the canvas. */
export function applyGuidesLockState(canvas: fabric.Canvas): void {
  canvas.getObjects().forEach((obj) => {
    if (!isGuideObject(obj)) return;
    const orientation = getGuideOrientation(obj);
    obj.set({
      selectable: !guidesLocked,
      evented: !guidesLocked,
      lockMovementX: guidesLocked || orientation === 'h',
      lockMovementY: guidesLocked || orientation === 'v',
    });
  });
  canvas.requestRenderAll();
}

export function isGuideObject(obj: fabric.Object | null | undefined): boolean {
  if (!obj) return false;
  const g = obj as GuideObject;
  return Boolean(g.craftaraGuide || g.pixkitGuide);
}

export function getGuideOrientation(
  obj: fabric.Object
): GuideOrientation | null {
  const g = obj as GuideObject;
  const o = g.craftaraGuide || g.pixkitGuide;
  return o === 'v' || o === 'h' ? o : null;
}

/** Create a cyan guide line across the artboard. */
export function createGuide(
  canvas: fabric.Canvas,
  orientation: GuideOrientation,
  position: number
): fabric.Line {
  const w = canvas.getWidth();
  const h = canvas.getHeight();
  const pos =
    orientation === 'v'
      ? Math.max(0, Math.min(w, Math.round(position)))
      : Math.max(0, Math.min(h, Math.round(position)));

  const coords =
    orientation === 'v' ? ([pos, 0, pos, h] as [number, number, number, number]) : ([0, pos, w, pos] as [number, number, number, number]);

  const line = new fabric.Line(coords, {
    stroke: 'rgba(13, 180, 168, 0.85)',
    strokeWidth: 1,
    selectable: !guidesLocked,
    evented: !guidesLocked,
    hasControls: false,
    hasBorders: false,
    lockMovementX: guidesLocked || orientation === 'h',
    lockMovementY: guidesLocked || orientation === 'v',
    lockScalingX: true,
    lockScalingY: true,
    lockRotation: true,
    hoverCursor: orientation === 'v' ? 'col-resize' : 'row-resize',
    moveCursor: orientation === 'v' ? 'col-resize' : 'row-resize',
    excludeFromExport: true,
    objectCaching: false,
  });

  (line as GuideObject)[GUIDE_KEY] = orientation;
  (line as fabric.Object & { name?: string }).name =
    orientation === 'v' ? 'V Guide' : 'H Guide';

  canvas.add(line);
  canvas.bringToFront(line);
  canvas.requestRenderAll();
  return line;
}

/** Keep guide axis-aligned and clamped while dragging. */
export function constrainGuideMove(obj: fabric.Object, canvas: fabric.Canvas): void {
  const orientation = getGuideOrientation(obj);
  if (!orientation) return;
  const w = canvas.getWidth();
  const h = canvas.getHeight();

  if (orientation === 'v') {
    const x = Math.max(0, Math.min(w, Math.round(obj.left || 0)));
    obj.set({ left: x, top: 0, angle: 0, scaleX: 1, scaleY: 1 });
  } else {
    const y = Math.max(0, Math.min(h, Math.round(obj.top || 0)));
    obj.set({ left: 0, top: y, angle: 0, scaleX: 1, scaleY: 1 });
  }
  obj.setCoords();
}

export function getGuidePositions(canvas: fabric.Canvas): {
  vertical: number[];
  horizontal: number[];
} {
  const vertical: number[] = [];
  const horizontal: number[] = [];
  canvas.getObjects().forEach((obj) => {
    const orientation = getGuideOrientation(obj);
    if (!orientation) return;
    const b = obj.getBoundingRect(true);
    if (orientation === 'v') {
      vertical.push(b.left + b.width / 2);
    } else {
      horizontal.push(b.top + b.height / 2);
    }
  });
  return { vertical, horizontal };
}

export function clearGuides(canvas: fabric.Canvas): number {
  const guides = canvas.getObjects().filter(isGuideObject);
  guides.forEach((g) => canvas.remove(g));
  canvas.requestRenderAll();
  return guides.length;
}

export function clientToArtboard(
  canvas: fabric.Canvas,
  clientX: number,
  clientY: number
): { x: number; y: number } {
  const el =
    (canvas as fabric.Canvas & { upperCanvasEl?: HTMLCanvasElement }).upperCanvasEl ||
    canvas.getElement();
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return { x: 0, y: 0 };
  }
  return {
    x: ((clientX - rect.left) / rect.width) * canvas.getWidth(),
    y: ((clientY - rect.top) / rect.height) * canvas.getHeight(),
  };
}

function eventClientPoint(e: Event): { x: number; y: number } | null {
  const any = e as MouseEvent & {
    touches?: TouchList;
    changedTouches?: TouchList;
  };
  if (typeof any.clientX === 'number' && typeof any.clientY === 'number') {
    return { x: any.clientX, y: any.clientY };
  }
  const t = any.touches?.[0] || any.changedTouches?.[0];
  if (t) return { x: t.clientX, y: t.clientY };
  return null;
}

type InteractiveCanvas = fabric.Canvas & {
  getPointer: (e: Event, ignoreZoom?: boolean) => { x: number; y: number };
  _pointer: { x: number; y: number } | null;
  _absolutePointer: { x: number; y: number } | null;
  _currentTransform: {
    target?: fabric.Object | null;
  } | null;
  _groupSelector: unknown;
  restorePointerVpt: (p: { x: number; y: number }) => { x: number; y: number };
  viewportTransform?: number[];
};

/**
 * Force-end a stuck drag/transform and clear selection.
 * Used when CSS-zoom pointer bugs leave `_currentTransform` active after mouseup,
 * and for Escape to always release the grabbed object.
 */
export function forceReleaseCanvas(canvas: fabric.Canvas): void {
  const c = canvas as InteractiveCanvas;
  const transform = c._currentTransform;
  if (transform?.target) {
    transform.target.setCoords();
    (transform.target as fabric.Object & { isMoving?: boolean }).isMoving = false;
  }
  c._currentTransform = null;
  c._groupSelector = null;
  c._pointer = null;
  c._absolutePointer = null;

  const active = canvas.getActiveObject() as
    | (fabric.Object & { isEditing?: boolean; exitEditing?: () => void })
    | null
    | undefined;
  if (active?.isEditing && typeof active.exitEditing === 'function') {
    active.exitEditing();
  }

  canvas.discardActiveObject();
  canvas.getObjects().forEach((obj) => {
    (obj as fabric.Object & { isMoving?: boolean }).isMoving = false;
  });
  canvas.requestRenderAll();
}

/**
 * Fabric's getPointer mis-handles CSS `transform: scale()` on `.canvas-container`
 * (used for editor zoom), especially with `transform-origin: top center`.
 * Object drag then "sticks" to the cursor / won't land where released.
 * Templates are large so users zoom out — the bug shows up there first.
 */
export function installCssZoomPointerFix(canvas: fabric.Canvas): () => void {
  const c = canvas as InteractiveCanvas;
  const original = c.getPointer.bind(c);

  c.getPointer = function patchedGetPointer(e: Event, ignoreZoom?: boolean) {
    try {
      if (c._absolutePointer && !ignoreZoom) return c._absolutePointer;
      if (c._pointer && ignoreZoom) return c._pointer;

      const client = eventClientPoint(e);
      if (!client) return original(e, ignoreZoom);

      const absolute = clientToArtboard(c, client.x, client.y);
      // Always return plain objects — Fabric Point instances can be mutated later
      if (!ignoreZoom) {
        return { x: absolute.x, y: absolute.y };
      }

      // Mirror Fabric: ignoreZoom=true returns pre-inverse-VPT coords
      const vt = c.viewportTransform || [1, 0, 0, 1, 0, 0];
      const [a, b, c0, d, e0, f] = vt;
      return {
        x: a * absolute.x + c0 * absolute.y + e0,
        y: b * absolute.x + d * absolute.y + f,
      };
    } catch {
      return original(e, ignoreZoom);
    }
  };

  return () => {
    c.getPointer = original;
  };
}
